import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Download, Wand2, Eye, Code, Loader2, LogIn, LogOut, User, AlertTriangle, Settings, BookOpen, History } from 'lucide-react';
import { useLogto } from '@logto/react';
import { ReservedResource } from '@logto/react';
import { redirectUri, postLogoutRedirectUri, APP_SCOPE, API_RESOURCE } from './logtoConfig';
import { parseJwtPayload } from './logtoUtils';
import { useNavigate } from 'react-router-dom';

function App() {
  const { signIn, signOut, isAuthenticated, isLoading: isLogtoLoading, getIdTokenClaims, getAccessToken, fetchUserInfo } = useLogto();
  const navigate = useNavigate();
  const [description, setDescription] = useState('');
  const [selectedLayout, setSelectedLayout] = useState('single-column');
  const [selectedColors, setSelectedColors] = useState('tech-blue');
  const [selectedProvider, setSelectedProvider] = useState('openrouter');
  const [layouts, setLayouts] = useState({});
  const [colorPalettes, setColorPalettes] = useState({});
  const [aiProviders, setAiProviders] = useState([]);
  const [generatedCode, setGeneratedCode] = useState({ html: '', css: '' });
  const [isGenerating, setIsGenerating] = useState(false);
  const [activeTab, setActiveTab] = useState('preview');
  const [error, setError] = useState('');
  const [userInfo, setUserInfo] = useState(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [permissions, setPermissions] = useState(null);
  const [hasAccess, setHasAccess] = useState(false);
  const [isLoadingPermissions, setIsLoadingPermissions] = useState(false); // 初始为 false，只有真正开始检查时才设为 true
  const [isAdmin, setIsAdmin] = useState(false);
  const [authChecked, setAuthChecked] = useState(false); // 标记认证是否已检查
  const permissionCheckingRef = useRef(false); // 防止权限检查并发/重复触发
  const permissionCheckedRef = useRef(false); // 当前会话内只完整检查一次
  const signInRedirectingRef = useRef(false); // 防止未登录时重复触发 signIn

  // 调试：在开发环境输出重定向URI信息
  useEffect(() => {
    if (process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost') {
      const origin = window.location.origin;
      let redirectUri;
      if (origin === 'http://localhost' || origin === 'http://localhost:80') {
        redirectUri = 'http://localhost:80/callback';
      } else {
        redirectUri = `${origin}/callback`;
      }
      console.log('🔍 Logto 调试信息:');
      console.log('当前Origin:', origin);
      console.log('使用的重定向URI:', redirectUri);
      console.log('退出登录后重定向URI:', redirectUri.replace('/callback', '/'));
      console.log('请在Logto控制台配置以上URI！');
    }
  }, []);

  // 检查登录状态、获取用户信息和权限
  useEffect(() => {
    const checkAuthAndPermissions = async () => {
      // 调试信息
      console.log('🔍 权限检查状态:', {
        isLogtoLoading,
        isAuthenticated,
        isLoadingPermissions,
        authChecked,
        hasAccess,
        checkingRef: permissionCheckingRef.current,
        checkedRef: permissionCheckedRef.current,
      });
      
      // 如果 Logto 认证状态未稳定且未认证，等待
      if (isLogtoLoading && !isAuthenticated) {
        console.log('⏳ 等待 Logto 认证状态稳定...');
        return; // Logto 认证状态未稳定，等待
      }
      
      // 如果已登录，进行权限检查
      if (isAuthenticated) {
        // 已登录后，重置登录跳转标记
        signInRedirectingRef.current = false;

        // 当前会话已检查完成，直接跳过，避免返回首页后循环刷日志
        if (permissionCheckedRef.current) {
          if (!authChecked) setAuthChecked(true);
          console.log('⏭️ 会话内权限已检查，跳过重复检查');
          return;
        }

        // 如果权限已经验证完成，跳过
        if (authChecked) {
          console.log('⏭️ 权限检查已完成，跳过');
          return;
        }
        
        // 如果权限正在验证中，等待
        if (isLoadingPermissions) {
          console.log('⏳ 权限检查正在进行中，等待完成...');
          return;
        }

        if (permissionCheckingRef.current) {
          console.log('⏳ 权限检查并发锁生效，跳过重复触发');
          return;
        }
        
        console.log('🚀 开始权限检查...');
        
        permissionCheckingRef.current = true;
        setIsLoadingPermissions(true);
        setHasAccess(false); // 重置权限状态，避免使用旧值
        // 不要在这里设置 authChecked，等权限验证完成后再设置
        
        let finalHasAccess = false; // 用于记录最终的权限检查结果
        
        try {
          // 获取用户信息
          const idTokenClaims = await getIdTokenClaims();
          const userInfoData = await fetchUserInfo();
          setUserInfo(idTokenClaims);

          // 获取组织列表
          const organizations = idTokenClaims?.organizations || [];
          const organizationData = userInfoData?.organization_data || [];
          const finalOrganizations = organizations.length > 0 ? organizations : organizationData;

          // 调试输出：组织信息
          console.log('🏢 组织信息调试:');
          console.log('  ID Token 组织:', organizations);
          console.log('  UserInfo 组织:', organizationData);
          console.log('  最终组织列表:', finalOrganizations);
          console.log('  组织数量:', finalOrganizations.length);

          let allScopes = new Set();
          let hasRequiredAccess = false;

          // 如果用户属于组织，获取组织权限和API资源权限
          if (finalOrganizations.length > 0) {
            const organizationId = typeof finalOrganizations[0] === 'string' 
              ? finalOrganizations[0] 
              : finalOrganizations[0].id;

            try {
              // 1. 获取组织权限（非API）
              try {
                const orgToken = await getAccessToken(ReservedResource.Organization, organizationId);
                if (orgToken) {
                  const orgClaims = parseJwtPayload(orgToken);
                  const orgScopes = orgClaims?.scope ? orgClaims.scope.split(' ').filter(Boolean) : [];
                  orgScopes.forEach(scope => allScopes.add(scope));
                }
              } catch (err) {
                console.warn('获取组织权限失败:', err);
              }

              // 2. 获取组织级API资源权限
              try {
                const apiToken = await getAccessToken(API_RESOURCE, organizationId);
                if (apiToken) {
                  const apiClaims = parseJwtPayload(apiToken);
                  const apiScopes = apiClaims?.scope ? apiClaims.scope.split(' ').filter(Boolean) : [];
                  apiScopes.forEach(scope => allScopes.add(scope));
                  hasRequiredAccess = apiScopes.includes(APP_SCOPE);
                }
              } catch (err) {
                console.warn('获取API资源权限失败:', err);
              }
            } catch (err) {
              console.error('获取权限失败:', err);
            }
          } else {
            // 用户不属于任何组织，尝试获取用户级别的API资源权限
            try {
              const apiToken = await getAccessToken(API_RESOURCE);
              if (apiToken) {
                const apiClaims = parseJwtPayload(apiToken);
                const apiScopes = apiClaims?.scope ? apiClaims.scope.split(' ').filter(Boolean) : [];
                apiScopes.forEach(scope => allScopes.add(scope));
                hasRequiredAccess = apiScopes.includes(APP_SCOPE);
              }
            } catch (err) {
              console.warn('获取用户级API资源权限失败:', err);
            }
          }

          // 如果还没有权限，检查ID Token中的权限
          if (!hasRequiredAccess && idTokenClaims?.scope) {
            const idTokenScopes = idTokenClaims.scope.split(' ').filter(Boolean);
            idTokenScopes.forEach(scope => allScopes.add(scope));
            hasRequiredAccess = idTokenScopes.includes(APP_SCOPE);
          }

          finalHasAccess = hasRequiredAccess; // 记录最终结果
          setHasAccess(hasRequiredAccess);
          
          // 检查是否有管理员权限
          const adminPermissions = ['manage:member', 'view:logs'];
          const hasAdminPermission = adminPermissions.some(perm => allScopes.has(perm));
          setIsAdmin(hasAdminPermission);
          
          setPermissions({
            allScopes: Array.from(allScopes),
            hasAccess: hasRequiredAccess,
            isAdmin: hasAdminPermission,
            organizationId: finalOrganizations.length > 0 
              ? (typeof finalOrganizations[0] === 'string' ? finalOrganizations[0] : finalOrganizations[0].id)
              : null,
          });

          // 打印权限信息
          console.log('%c═══════════════════════════════════════════════════════', 'color: #667eea; font-weight: bold;');
          console.log('%c📋 Logto 权限信息', 'color: #667eea; font-weight: bold; font-size: 16px;');
          console.log('%c═══════════════════════════════════════════════════════', 'color: #667eea; font-weight: bold;');
          console.log('%c👤 用户信息', 'color: #4CAF50; font-weight: bold;');
          console.log('用户ID:', idTokenClaims?.sub || userInfoData?.sub || '(未获取)');
          console.log('用户名:', userInfoData?.username || '(未设置)');
          console.log('姓名:', userInfoData?.name || '(未设置)');
          console.log('邮箱:', userInfoData?.primaryEmail || userInfoData?.email || '(未设置)');
          console.log('%c🏢 组织信息', 'color: #FF9800; font-weight: bold;');
          if (finalOrganizations.length > 0) {
            console.log('组织数量:', finalOrganizations.length);
            console.log('组织列表:', finalOrganizations);
            finalOrganizations.forEach((org, index) => {
              if (typeof org === 'string') {
                console.log(`  组织 ${index + 1}: ${org}`);
              } else {
                console.log(`  组织 ${index + 1}:`, {
                  ID: org.id,
                  名称: org.name || '(未设置)',
                  描述: org.description || '(未设置)',
                });
              }
            });
          } else {
            console.log('组织: 未加入任何组织');
          }
          console.log('%c📊 权限汇总', 'color: #9C27B0; font-weight: bold;');
          console.log('所有权限列表:', Array.from(allScopes));
          console.log('总权限数:', allScopes.size);
          console.log(`是否有 ${APP_SCOPE} 权限:`, hasRequiredAccess ? '✅ 是' : '❌ 否');
          console.log('%c═══════════════════════════════════════════════════════', 'color: #667eea; font-weight: bold;');
        } catch (err) {
          console.error('❌ 获取用户信息和权限失败:', err);
          finalHasAccess = false;
          setHasAccess(false);
        } finally {
          console.log('✅ 权限检查完成，设置状态:', {
            hasAccess: finalHasAccess,
            isLoadingPermissions: false,
            authChecked: true
          });
          permissionCheckingRef.current = false;
          permissionCheckedRef.current = true;
          setIsLoadingPermissions(false);
          setAuthChecked(true); // 权限验证完成后才标记认证已检查
        }
      } else {
        // 未登录时，标记认证未完成，并自动跳转到登录页面
        permissionCheckingRef.current = false;
        permissionCheckedRef.current = false;
        setAuthChecked(false);
        setIsLoadingPermissions(false);
        if (!signInRedirectingRef.current) {
          signInRedirectingRef.current = true;
          try {
            await signIn(redirectUri);
          } catch (err) {
            signInRedirectingRef.current = false;
            console.error('登录失败:', err);
          }
        }
      }
    };

    checkAuthAndPermissions();
  }, [isAuthenticated, isLogtoLoading, getIdTokenClaims, fetchUserInfo, getAccessToken, signIn]);

  // 获取布局、色系和AI提供商配置
  useEffect(() => {
    const fetchConfigs = async () => {
      // 只有登录后才获取配置
      if (!isAuthenticated) {
        return;
      }

      try {
        const [layoutsRes, colorsRes, providersRes] = await Promise.all([
          axios.get('/api/layouts'),
          axios.get('/api/colors'),
          axios.get('/api/ai-providers')
        ]);
        setLayouts(layoutsRes.data);
        setColorPalettes(colorsRes.data);
        const providers = providersRes.data.providers || [];
        setAiProviders(providers);
        const def = providersRes.data.defaultProvider;
        const ids = new Set(providers.map((p) => p.id));
        if (def && ids.has(def)) {
          setSelectedProvider(def);
        } else if (ids.has('openrouter')) {
          setSelectedProvider('openrouter');
        } else if (ids.has('siliconflow')) {
          setSelectedProvider('siliconflow');
        } else if (providers.length > 0) {
          setSelectedProvider(providers[0].id);
        } else {
          setSelectedProvider('ollama');
        }
      } catch (err) {
        console.error('获取配置失败:', err);
        const isNetwork =
          !err.response ||
          err.code === 'ERR_NETWORK' ||
          err.message === 'Network Error';
        setError(
          isNetwork
            ? '无法连接到服务器（若刚访问「我的历史」，可能被防护临时封禁约 10 分钟，请稍后再刷新）'
            : `获取配置失败: ${err.response?.data?.error || err.message}`
        );
      }
    };

    fetchConfigs();
  }, [isAuthenticated]);

  // 生成网页
  const handleGenerate = async () => {
    if (!description.trim()) {
      setError('请输入网页描述');
      return;
    }

    setIsGenerating(true);
    setError('');

    try {
      // 获取 access token 用于 API 认证
      // 如果用户属于组织，使用“组织级 API 资源 Token”，确保后端能拿到组织上下文并正确写入历史记录
      const orgId = permissions?.organizationId || null;
      const token = orgId
        ? await getAccessToken(API_RESOURCE, orgId)
        : await getAccessToken(API_RESOURCE);
      
      const response = await axios.post('/api/generate', {
        description: description.trim(),
        layout: selectedLayout,
        colors: selectedColors,
        provider: selectedProvider
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
          // 兜底：即使 Token 不带组织信息，也把组织ID显式传给后端用于写入 history.organization_id
          ...(orgId ? { 'X-Organization-Id': orgId } : {})
        }
      });

      setGeneratedCode({
        html: response.data.html,
        css: response.data.css
      });
      setActiveTab('preview');
    } catch (err) {
      console.error('生成失败:', err);
      setError('生成网页失败，请稍后重试');
    } finally {
      setIsGenerating(false);
    }
  };

  // 下载代码
  const handleDownload = async () => {
    if (!generatedCode.html || !generatedCode.css) {
      setError('没有可下载的代码');
      return;
    }

    try {
      // 直接导出单个 HTML 文件，并将 CSS 内联，保证离线可打开
      const standaloneHtml = generatedCode.html.replace(
        '<link rel="stylesheet" href="style.css">',
        `<style>${generatedCode.css}</style>`
      );
      const blob = new Blob([standaloneHtml], { type: 'text/html;charset=utf-8' });

      // 创建下载链接
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'magic-page.html');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('下载失败:', err);
      setError('下载失败，请稍后重试');
    }
  };

  // 创建预览iframe的内容
  const getPreviewContent = () => {
    if (!generatedCode.html || !generatedCode.css) {
      return '<div style="display: flex; align-items: center; justify-content: center; height: 100vh; font-family: Arial, sans-serif; color: #666;">请先生成网页代码</div>';
    }

    // 将CSS内联到HTML中以便在iframe中显示
    const htmlWithInlineCSS = generatedCode.html.replace(
      '<link rel="stylesheet" href="style.css">',
      `<style>${generatedCode.css}</style>`
    );

    return htmlWithInlineCSS;
  };

  // 处理登出
  const handleSignOut = async () => {
    try {
      await signOut(postLogoutRedirectUri);
    } catch (err) {
      console.error('登出失败:', err);
      // 即使登出失败，也清除本地状态
      setUserInfo(null);
      window.location.href = postLogoutRedirectUri;
    }
  };

  // 点击外部关闭用户菜单
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showUserMenu && !event.target.closest('.user-menu-container')) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserMenu]);

  // 步骤1: Logto 认证状态未稳定且未认证，显示加载状态
  if (isLogtoLoading && !isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">正在验证身份...</p>
        </div>
      </div>
    );
  }

  // 步骤2: 未登录，显示加载状态（会立即跳转到 Logto）
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">正在跳转到登录页面...</p>
        </div>
      </div>
    );
  }

  // 步骤3: 已登录，但认证未稳定或正在验证权限，显示加载状态
  // 使用 authChecked 来避免频繁切换导致的闪烁
  if (!authChecked || isLoadingPermissions) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">正在验证权限...</p>
        </div>
      </div>
    );
  }

  // 步骤4: 权限验证完成后，检查权限
  // 已登录但无权限 - 只有在权限验证完成后才显示
  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <AlertTriangle className="w-16 h-16 text-orange-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-4">访问受限</h2>
          <p className="text-gray-600 mb-6">
            您没有访问此应用的权限。请联系管理员为您分配使用权限。
          </p>
          <button
            onClick={() => signOut(postLogoutRedirectUri)}
            className="w-full px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center space-x-2"
          >
            <LogOut className="w-4 h-4" />
            <span>退出登录</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 头部 */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-2">
              <Wand2 className="h-8 w-8 text-blue-600" />
              <h1 className="text-2xl font-bold text-gray-900">Magic Page</h1>
              <span className="text-sm text-gray-500">AI网页生成器</span>
            </div>
            
            {/* 右侧按钮区域 */}
            {userInfo && (
              <div className="flex items-center space-x-4">
                {/* 我的历史 */}
                <button
                  onClick={() => navigate('/history')}
                  className="flex items-center space-x-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  title="我的历史"
                >
                  <History className="w-4 h-4" />
                  <span className="hidden sm:inline">我的历史</span>
                </button>

                {/* 使用手册按钮 */}
                <button
                  onClick={() => navigate('/manual')}
                  className="flex items-center space-x-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  title="使用手册"
                >
                  <BookOpen className="w-4 h-4" />
                  <span className="hidden sm:inline">使用手册</span>
                </button>

                {/* 组织管理按钮（仅管理员可见） */}
                {isAdmin && (
                  <button
                    onClick={() => navigate('/admin')}
                    className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all shadow-md hover:shadow-lg"
                  >
                    <Settings className="w-4 h-4" />
                    <span className="hidden sm:inline">组织管理</span>
                  </button>
                )}
                
                {/* 用户信息 */}
                <div className="relative user-menu-container">
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="flex items-center space-x-2 px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <User className="h-5 w-5 text-gray-600" />
                    <span className="text-sm font-medium text-gray-700">
                      {userInfo.name || userInfo.username || userInfo.email || '用户'}
                    </span>
                  </button>
                  {showUserMenu && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                      <button
                        onClick={() => {
                          navigate('/history');
                          setShowUserMenu(false);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                      >
                        <History className="h-4 w-4" />
                        <span>我的历史</span>
                      </button>
                      {isAdmin && (
                        <button
                          onClick={() => {
                            navigate('/admin');
                            setShowUserMenu(false);
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                        >
                          <Settings className="h-4 w-4" />
                          <span>组织管理</span>
                        </button>
                      )}
                      <button
                        onClick={handleSignOut}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                      >
                        <LogOut className="h-4 w-4" />
                        <span>退出登录</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      <div className="h-[calc(100vh-4rem)] flex flex-col lg:flex-row overflow-hidden">
        {/* 左侧控制面板 - 25% */}
        <div className="w-full lg:w-1/4 bg-gray-50 border-b lg:border-b-0 lg:border-r border-gray-200 overflow-y-auto max-h-96 lg:max-h-none">
          <div className="p-6 space-y-6">
            {/* 错误提示 */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-700">{error}</p>
              </div>
            )}

            {/* 内容描述输入 */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                网页内容描述
              </h2>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="请描述您想要创建的网页内容，支持丰富格式：&#10;&#10;🎯 智能识别示例：&#10;# 创新科技公司&#10;我们是一家专注于AI技术的创新企业&#10;&#10;## 核心服务&#10;- 人工智能咨询&#10;- 机器学习解决方案&#10;- 数据分析服务&#10;&#10;📞 联系方式：&#10;邮箱: contact@example.com&#10;电话: 400-123-4567&#10;网站: https://www.example.com&#10;&#10;🖼️ 展示图片：&#10;https://example.com/team-photo.jpg&#10;&#10;💡 支持URL、邮箱、电话、图片等自动识别和美化"
                className="w-full h-40 p-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={isGenerating}
              />
            </div>

            {/* 布局选择 */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                🎨 专业版式选择
              </h2>
              <div className="space-y-4">
                {/* 按类别分组显示布局 */}
                {Object.entries(
                  Object.entries(layouts).reduce((acc, [key, layout]) => {
                    const category = layout.category || '通用';
                    if (!acc[category]) acc[category] = [];
                    acc[category].push([key, layout]);
                    return acc;
                  }, {})
                ).map(([category, categoryLayouts]) => (
                  <div key={category}>
                    <h3 className="text-sm font-medium text-gray-700 mb-2 px-1">
                      {category}
                    </h3>
                    <div className="grid grid-cols-1 gap-2">
                      {categoryLayouts.map(([key, layout]) => (
                        <label key={key} className="cursor-pointer">
                          <input
                            type="radio"
                            name="layout"
                            value={key}
                            checked={selectedLayout === key}
                            onChange={(e) => setSelectedLayout(e.target.value)}
                            className="sr-only"
                            disabled={isGenerating}
                          />
                          <div className={`p-3 border-2 rounded-lg transition-colors ${
                            selectedLayout === key
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}>
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <div className="font-medium text-gray-900">{layout.name}</div>
                                <div className="text-sm text-gray-500 mt-1">{layout.description}</div>
                              </div>
                              {selectedLayout === key && (
                                <div className="flex-shrink-0 ml-2">
                                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                </div>
                              )}
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* 色系选择 */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                🎨 专业配色方案
              </h2>
              <div className="space-y-4">
                {/* 按类别分组显示配色 */}
                {Object.entries(
                  Object.entries(colorPalettes).reduce((acc, [key, palette]) => {
                    const category = palette.category || '通用';
                    if (!acc[category]) acc[category] = [];
                    acc[category].push([key, palette]);
                    return acc;
                  }, {})
                ).map(([category, categoryPalettes]) => (
                  <div key={category}>
                    <h3 className="text-sm font-medium text-gray-700 mb-2 px-1">
                      {category}
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {categoryPalettes.map(([key, palette]) => (
                        <label key={key} className="cursor-pointer">
                          <input
                            type="radio"
                            name="colors"
                            value={key}
                            checked={selectedColors === key}
                            onChange={(e) => setSelectedColors(e.target.value)}
                            className="sr-only"
                            disabled={isGenerating}
                          />
                          <div className={`p-3 border-2 rounded-lg transition-colors ${
                            selectedColors === key
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}>
                            <div className="flex items-center space-x-2 mb-2">
                              <div 
                                className="w-4 h-4 rounded-full border border-gray-200"
                                style={{ backgroundColor: palette.primary }}
                              ></div>
                              <div 
                                className="w-4 h-4 rounded-full border border-gray-200"
                                style={{ backgroundColor: palette.secondary }}
                              ></div>
                              <div 
                                className="w-4 h-4 rounded-full border border-gray-200"
                                style={{ backgroundColor: palette.accent }}
                              ></div>
                              {selectedColors === key && (
                                <div className="flex-1 flex justify-end">
                                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                </div>
                              )}
                            </div>
                            <div className="font-medium text-gray-900">{palette.name}</div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* AI算力选择 - 已隐藏，默认使用OpenRouter */}
            {false && aiProviders.length > 0 && (
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  🤖 AI算力选择
                </h2>
                <div className="space-y-3">
                  {aiProviders.map((provider) => (
                    <label key={provider.id} className="cursor-pointer">
                      <input
                        type="radio"
                        name="provider"
                        value={provider.id}
                        checked={selectedProvider === provider.id}
                        onChange={(e) => setSelectedProvider(e.target.value)}
                        className="sr-only"
                        disabled={isGenerating}
                      />
                      <div className={`p-3 border-2 rounded-lg transition-colors ${
                        selectedProvider === provider.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}>
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="font-medium text-gray-900 mb-1">
                              {provider.name}
                              {provider.id === 'ollama' && (
                                <span className="ml-2 px-2 py-1 text-xs bg-green-100 text-green-800 rounded">
                                  本地
                                </span>
                              )}
                              {provider.id === 'openrouter' && (
                                <span className="ml-2 px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded">
                                  多模型
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-gray-500 mb-2">
                              {provider.description}
                            </div>
                            <div className="text-xs text-gray-400">
                              模型: {provider.models.slice(0, 2).join(', ')}
                              {provider.models.length > 2 && ` +${provider.models.length - 2}更多`}
                            </div>
                          </div>
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
                <div className="mt-3 p-2 bg-yellow-50 rounded-lg">
                  <p className="text-xs text-yellow-800">
                    💡 提示: Ollama 需本地安装；OpenRouter / OpenAI / SiliconFlow 需在环境变量中配置对应 API Key
                  </p>
                </div>
              </div>
            )}
            
            {/* AI算力状态显示 */}
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                <span className="text-sm font-medium text-purple-800">
                  AI算力: 云端算力支持
                </span>
              </div>
              <p className="text-xs text-purple-600 mt-1">
                使用专业代码生成模型进行网页创作
              </p>
            </div>

            {/* 生成按钮 */}
            <button
              onClick={handleGenerate}
              disabled={isGenerating || !description.trim()}
              className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <span>正在生成...</span>
                </>
              ) : (
                <>
                  <Wand2 className="h-5 w-5" />
                  <span>一键生成</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* 右侧预览区域 - 75% */}
        <div className="w-full lg:w-3/4 flex flex-col bg-white flex-1">
          {/* 标签切换 - 固定高度 */}
          <div className="flex-shrink-0 border-b border-gray-200 bg-white">
            <nav className="flex flex-wrap">
                <button
                  onClick={() => setActiveTab('preview')}
                  className={`py-3 px-3 lg:px-6 text-sm font-medium border-b-2 flex-shrink-0 ${
                    activeTab === 'preview'
                      ? 'border-blue-500 text-blue-600 bg-blue-50'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Eye className="inline h-4 w-4 mr-1 lg:mr-2" />
                  <span className="hidden sm:inline">实时</span>预览
                </button>
                <button
                  onClick={() => setActiveTab('html')}
                  className={`py-3 px-3 lg:px-6 text-sm font-medium border-b-2 flex-shrink-0 ${
                    activeTab === 'html'
                      ? 'border-blue-500 text-blue-600 bg-blue-50'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Code className="inline h-4 w-4 mr-1 lg:mr-2" />
                  HTML<span className="hidden sm:inline">源码</span>
                </button>
                <button
                  onClick={() => setActiveTab('css')}
                  className={`py-3 px-3 lg:px-6 text-sm font-medium border-b-2 flex-shrink-0 ${
                    activeTab === 'css'
                      ? 'border-blue-500 text-blue-600 bg-blue-50'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Code className="inline h-4 w-4 mr-1 lg:mr-2" />
                  CSS<span className="hidden sm:inline">样式</span>
                </button>
                
                {/* 下载按钮移到标签栏右侧 */}
                <div className="ml-auto flex items-center px-2 lg:px-4">
                  <button
                    onClick={handleDownload}
                    disabled={!generatedCode.html || !generatedCode.css}
                    className="bg-green-600 text-white py-2 px-2 lg:px-4 rounded-md font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1 lg:space-x-2 text-sm"
                  >
                    <Download className="h-4 w-4" />
                    <span className="hidden sm:inline">下载</span><span className="sm:hidden">↓</span>
                  </button>
                </div>
              </nav>
          </div>

          {/* 主预览区域 - 占满剩余空间 */}
          <div className="flex-1 relative bg-gray-50">
            {/* 预览iframe - 最大化高度 */}
            {activeTab === 'preview' && (
              <div className="absolute inset-0 p-1">
                <div className="bg-white rounded-lg shadow-sm border h-full overflow-hidden">
                  <iframe
                    title="网页预览"
                    srcDoc={getPreviewContent()}
                    className="w-full h-full rounded-lg border-0"
                    sandbox="allow-same-origin allow-scripts"
                  />
                </div>
              </div>
            )}

            {/* HTML代码 - 最大化高度 */}
            {activeTab === 'html' && (
              <div className="absolute inset-0 p-1">
                <div className="bg-white rounded-lg shadow-sm border h-full overflow-hidden">
                  <div className="h-full overflow-auto">
                    <pre className="p-3 text-sm leading-relaxed font-mono">
                      <code className="language-html">{generatedCode.html || '<!-- HTML代码将在这里显示 -->'}</code>
                    </pre>
                  </div>
                </div>
              </div>
            )}

            {/* CSS代码 - 最大化高度 */}
            {activeTab === 'css' && (
              <div className="absolute inset-0 p-1">
                <div className="bg-white rounded-lg shadow-sm border h-full overflow-hidden">
                  <div className="h-full overflow-auto">
                    <pre className="p-3 text-sm leading-relaxed font-mono">
                      <code className="language-css">{generatedCode.css || '/* CSS代码将在这里显示 */'}</code>
                    </pre>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;